import type { PDFDocumentProxy } from "pdfjs-dist";
import { loadFloorplanPdfEngine } from "./floorplanPdfEngine";
import { floorplanRasterScale } from "./floorplanRasterScale";

export async function openFloorplanPdf(file: File): Promise<PDFDocumentProxy> {
  if (file.size > 20 * 1024 * 1024)
    throw new Error("Choose a PDF smaller than 20 MB");
  const pdfjs = await loadFloorplanPdfEngine();
  const task = pdfjs.getDocument({
    data: new Uint8Array(await file.arrayBuffer()),
    isEvalSupported: false
  });
  try {
    return await task.promise;
  } catch (error) {
    await task.destroy();
    if (error instanceof Error && error.name === "PasswordException")
      throw new Error(
        "This PDF is password protected. Upload an unlocked copy."
      );
    throw new Error(
      "This PDF could not be read. Choose a valid PDF or export the page as an image."
    );
  }
}

export async function renderFloorplanPdfPage(
  pdf: PDFDocumentProxy,
  pageNumber: number,
  signal: AbortSignal
): Promise<Blob> {
  signal.throwIfAborted();
  if (
    !Number.isInteger(pageNumber) ||
    pageNumber < 1 ||
    pageNumber > pdf.numPages
  )
    throw new Error("Choose a valid PDF page");
  const page = await pdf.getPage(pageNumber);
  if (signal.aborted) {
    page.cleanup();
    signal.throwIfAborted();
  }
  const base = page.getViewport({ scale: 1 });
  if (![base.width, base.height].every((n) => Number.isFinite(n) && n > 0)) {
    page.cleanup();
    throw new Error("This PDF page has no dimensions");
  }
  const scale = floorplanRasterScale(base.width, base.height);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.floor(viewport.width));
  canvas.height = Math.max(1, Math.floor(viewport.height));
  const task = page.render({ canvas, viewport, background: "white" });
  const cancel = () => task.cancel();
  signal.addEventListener("abort", cancel, { once: true });
  try {
    await task.promise;
    signal.throwIfAborted();
    const png = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/png")
    );
    signal.throwIfAborted();
    if (!png || png.size > 12 * 1024 * 1024)
      throw new Error(
        "Rendered PDF page is too large. Export a smaller crop as an image."
      );
    return png;
  } finally {
    signal.removeEventListener("abort", cancel);
    canvas.width = 0;
    canvas.height = 0;
    page.cleanup();
  }
}
